import {Box, Typography} from '@mui/material';
import {FC, useState} from 'react';
import {organizationInfoStyle} from './OrganizationInfoStyle.styles';
import Button from '@/shared/components/Button';
import {BODY2, SUBTITLE1, TEXT} from '@/utils/constants';
import Modal from '@/shared/components/Modal';
import {ReactComponent as DeactivateIcon} from '@icons/shield-cross.svg';
import {ReactComponent as RestoreIcon} from '@icons/shield-tick.svg';
import {IAdminOrganizationResponse} from '@/utils/types/superAdmin.interface';
import dayjs from 'dayjs';
import {organizationsTableStyle} from '@/pages/Admin/Organizations/OrganizationTableStyle.styles';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {SuperAdminService} from '@/utils/services';
import {IAdminOrganizationStatus} from '@/pages/Admin/Organizations';

interface OrganizationDetailsProps {
  organization: IAdminOrganizationResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}

const OrganizationInfo: FC<OrganizationDetailsProps> = ({organization, onClose, onSuccess}) => {
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);

  const handleOpenChangeStatusModal = () => {
    setIsChangeStatusModalOpen(true);
  };

  const handleRestoreOrganization = async () => {
    try {
      await SuperAdminService.updateOrganizationStatus({id: organization?.id || 0, newStatus: 2});

      setIsChangeStatusModalOpen(false);
      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const handleDeactivateOrganization = async () => {
    try {
      await SuperAdminService.updateOrganizationStatus({id: organization?.id || 0, newStatus: 3});

      setIsChangeStatusModalOpen(false);
      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Box sx={organizationInfoStyle.wrapper}>
      <Box sx={organizationInfoStyle.container}>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Status</Typography>
          <Typography
            sx={{
              color: organization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? '#FF3D71' : '#00B74D',
            }}
          >
            ● {organization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? 'Deactivated' : 'Active'}
          </Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Transaction percent (%)</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.transactionFee ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Email</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.email ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Phone number</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.phoneNumber ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Address</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.address ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>City</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.city ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>State</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.state ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Zip</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.zip ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Tax ID number</Typography>
          <Typography sx={organizationInfoStyle.value}>{organization?.taxIdNumber ?? '-'}</Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Added</Typography>
          <Typography sx={organizationInfoStyle.value}>
            {dayjs.utc(organization?.added).format('MM.DD.YYYY') ?? '-'}
          </Typography>
        </Box>
        <Box sx={organizationInfoStyle.infoRow}>
          <Typography sx={organizationInfoStyle.label}>Edited</Typography>
          <Typography sx={organizationInfoStyle.value}>
            {dayjs.utc(organization?.edited).format('MM.DD.YYYY') ?? '-'}
          </Typography>
        </Box>
      </Box>
      <Box sx={organizationInfoStyle.restoreContainer}>
        <Box sx={organizationInfoStyle.restoreTextContainer}>
          <Typography variant={SUBTITLE1}>
            {organization?.organizationStatus === IAdminOrganizationStatus.Deactivated
              ? 'Restore Account'
              : 'Deactivate Account'}
          </Typography>
          <Typography sx={organizationInfoStyle.restoreText} variant={BODY2}>
            {organization?.organizationStatus === IAdminOrganizationStatus.Deactivated
              ? 'This account is now deactivated. You can restore it at any time.'
              : 'This account is now active. You can deactivate it at any time.'}
          </Typography>
        </Box>
        <Button
          title={
            organization?.organizationStatus === IAdminOrganizationStatus.Deactivated
              ? 'Restore Account'
              : 'Deactivate Account'
          }
          variant={TEXT}
          color={organization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? 'success' : 'error'}
          onClick={handleOpenChangeStatusModal}
        />
      </Box>

      <Modal
        mainButtonTitle={
          organization?.organizationStatus === IAdminOrganizationStatus.Deactivated
            ? 'Restore Account'
            : 'Deactivate Account'
        }
        closeButtonTitle="Cancel"
        open={isChangeStatusModalOpen}
        onClose={() => setIsChangeStatusModalOpen(false)}
        onSave={
          organization?.organizationStatus === IAdminOrganizationStatus.Deactivated
            ? handleRestoreOrganization
            : handleDeactivateOrganization
        }
        mainIcon={
          organization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? (
            <RestoreIcon />
          ) : (
            <DeactivateIcon />
          )
        }
        sx={organizationsTableStyle.modal}
        title={
          organization?.organizationStatus === IAdminOrganizationStatus.Deactivated
            ? 'Restore Organization'
            : 'Deactivate Organization'
        }
        color={organization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? 'primary' : 'error'}
        isShowCloseButton
        isShowSaveButton
      >
        {organization?.organizationStatus === IAdminOrganizationStatus.Deactivated ? (
          <Box sx={organizationsTableStyle.textWrapper}>
            <Typography sx={organizationsTableStyle.description}>
              <Typography sx={organizationsTableStyle.bold}>{organization?.email}</Typography> will be restored.
              <Typography sx={organizationsTableStyle.bold}>{organization?.name}</Typography> will be notified via
              email. It may take up to 30 minutes for the changes to take place.
            </Typography>

            <Typography sx={organizationsTableStyle.descriptionBottom}>
              Are you sure you want to restore this account?
            </Typography>
          </Box>
        ) : (
          <Box sx={organizationsTableStyle.textWrapper}>
            <Typography sx={organizationsTableStyle.boldCenter}>{organization?.email}</Typography>
            <Typography sx={organizationsTableStyle.description}>
              will no longer be able to access to Event Simpli.
            </Typography>
            <Typography sx={organizationsTableStyle.descriptionBottom}>
              Are you sure you want to deactivate this account?
            </Typography>
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default OrganizationInfo;
